/**
 * Soft≠Live Page186 — Response Incident Cell residual liquid
 * (Page107 buses + Page154 Soft≠Live posture retained).
 * Mount: /response/cell
 *
 * Buses (isolated — fail closed; no Soft invent cascade):
 *   GET /response/cell/status   always-on Response posture SoT
 *   GET /swarm/status           sister deployment flag (kept Live)
 *   GET /incidents              when incidents_list_allowed
 *   Cell unit · cluster team · trail/audit when admin buses + engine armed
 *
 * Soft residual deleted:
 *  - Soft joined members invent when flag off
 *  - Soft unarmed only from cell/cluster 503 guessing (status is SoT)
 *  - Soft stand-down role theatre without Live
 *  - Soft trail mark invent without POST
 *
 * Rollback: drop residual + /response/cell/status; pins prev v285/167.
 * Actuator sister desks stay Live registry / dry_run only.
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Sec, go, cmcPlainOf } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { softResponseErrorOf } from "./response-live.js";
import { useSession } from "./session.js";

/** Soft≠Live Page191: Live public_status nests {status, resource, dispatch_certified}. */
export function actuatorStatusPlainOf(row) {
  const r = row && typeof row === "object" ? row : {};
  const st = r.status;
  if (st != null && typeof st === "object") {
    const core = st.status != null ? String(st.status) : cmcPlainOf(st);
    if (st.dispatch_certified === true) return `${core} · certified`;
    if (st.dispatch_certified === false) return `${core} · not certified`;
    return core;
  }
  return cmcPlainOf(st != null ? st : r.state);
}

export const CLUSTER_ROLES = ["scout", "perimeter", "responder", "recorder", "command"];

export const STIGMERGY_TRACE_TYPES = [
  "scout_arrived",
  "perimeter_secured",
  "subject_located",
  "escalation_needed",
  "all_clear",
];

const PRIORITIES = ["normal", "elevated", "intrusion", "critical"];

/** Soft≠Live Page186: missing contract ops must not crash the desk. */
function safeCellPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_cell_status") return "/response/cell/status";
    if (op === "get__swarm_status") return "/swarm/status";
    if (op === "get__incidents") {
      const lim = (opts && opts.query && opts.query.limit) || 40;
      return `/incidents?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__incidents_p_cell") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cell`;
    }
    if (op === "get__incidents_p_cluster") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster`;
    }
    if (op === "get__incidents_p_cluster_affinity") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster/affinity`;
    }
    if (op === "get__incidents_p_stigmergy") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/stigmergy`;
    }
    if (op === "get__incidents_p_swarm-audit") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/swarm-audit`;
    }
    if (op === "post__incidents_p_cell_activate") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cell/activate`;
    }
    if (op === "post__incidents_p_cell_resolve") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cell/resolve`;
    }
    if (op === "post__incidents_p_cluster_join") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster/join`;
    }
    if (op === "post__incidents_p_cluster_leave") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster/leave`;
    }
    if (op === "post__incidents_p_cluster_reallocate") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster/reallocate`;
    }
    if (op === "patch__incidents_p_cluster_priority") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/cluster/priority`;
    }
    if (op === "post__incidents_p_stigmergy") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/stigmergy`;
    }
    return "";
  }
}

/** Pure: seat chips from Live Response Cell posture (Page186). */
export function responseCellSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const iMax = Number(lim.incidents_max);
  const iDef = Number(lim.incidents_default);
  const mMax = Number(lim.members_paint_max);
  const tMax = Number(lim.trail_paint_max);
  const aMax = Number(lim.audit_paint_max);
  return {
    incidentsListAllowed: p.incidents_list_allowed === true,
    cellReadAllowed: p.cell_read_allowed === true,
    activateAllowed: p.activate_allowed === true,
    resolveAllowed: p.resolve_allowed === true,
    clusterReadAllowed: p.cluster_read_allowed === true,
    joinAllowed: p.join_allowed === true,
    leaveAllowed: p.leave_allowed === true,
    reallocateAllowed: p.reallocate_allowed === true,
    priorityAllowed: p.priority_allowed === true,
    stigmergyReadAllowed: p.stigmergy_read_allowed === true,
    stigmergyWriteAllowed: p.stigmergy_write_allowed === true,
    auditReadAllowed: p.audit_read_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    writeAllowed: p.write_allowed === true,
    engineArmed: p.engine_armed === true || p.swarm_armed === true
      || (p.enabled === true && p.surface === "swarm_status"),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    incidentsLimit:
      Number.isFinite(iDef) && iDef > 0
        ? Math.min(iDef, Number.isFinite(iMax) && iMax > 0 ? iMax : 120)
        : 40,
    membersPaintMax: Number.isFinite(mMax) && mMax > 0 ? Math.floor(mMax) : 64,
    trailPaintMax: Number.isFinite(tMax) && tMax > 0 ? Math.floor(tMax) : 24,
    auditPaintMax: Number.isFinite(aMax) && aMax > 0 ? Math.floor(aMax) : 32,
  };
}

/** Pure: normalize cluster payload membership without inventing units. */
export function clusterMembersOf(clusterPack) {
  const item = (clusterPack && (clusterPack.item || clusterPack)) || {};
  const cluster = item.cluster || item;
  const members = Array.isArray(cluster.membership)
    ? cluster.membership
    : (Array.isArray(cluster.members) ? cluster.members : []);
  const roles = Array.isArray(cluster.role_allocations) ? cluster.role_allocations : [];
  return { cluster, members, roles, priority: cluster.priority || item.priority || "—" };
}

/** Pure: audit rows from swarm-audit envelope. */
export function auditRowsOf(auditPack) {
  const root = auditPack || {};
  const rows = root.items || root.rows || root.audit || (root.item && root.item.items) || [];
  return Array.isArray(rows) ? rows : [];
}

/** Pure: stigmergy traces. */
export function stigmergyRowsOf(stigPack) {
  const root = stigPack || {};
  const rows = root.items || root.traces || (root.item && root.item.traces) || [];
  return Array.isArray(rows) ? rows : [];
}

function IncidentPicker({ rows, incidentId, onPick, loading, error, sealed }) {
  return html`
    <${Card} title="Incidents" meta="catalog">
      ${sealed
        ? html`<p className="note">Incidents catalog sealed.</p>`
        : loading && !rows.length
          ? html`<p className="note">Loading…</p>`
          : error
            ? html`<${Alert} tone="warn" title="Could not load incidents" say=${softResponseErrorOf(error)}/>`
            : html`<div className="rows">
                ${!rows.length ? html`<p className="note">No incidents for this site yet.</p>` : null}
                ${rows.map((r) => {
                  const id = String(r.incident_id || r.id || "").trim();
                  return html`
                    <button key=${id} type="button" className="row" data-state=${incidentId===id?"on":"off"}
                      onClick=${()=>onPick(r)}>
                      <span className="st"/>
                      <span className="main">
                        <b>${id}</b>
                        <span>${cmcPlainOf(r.status || r.state || "—")} · ${r.device_id || "no device"}</span>
                      </span>
                    </button>`;
                })}
              </div>`}
    <//>`;
}

/* ── Swarm: cell + cluster + stigmergy + audit ─────────────────────────── */
export function SwarmDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const { run, busy, error, envelope } = useAction();
  const [incidentId, setIncidentId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [alarmId, setAlarmId] = useState("");
  const [pane, setPane] = useState("unit");
  const [resourceId, setResourceId] = useState("");
  const [resourceKind, setResourceKind] = useState("device");
  const [caps, setCaps] = useState("snapshot,siren");
  const [priority, setPriority] = useState("elevated");
  const [traceType, setTraceType] = useState(STIGMERGY_TRACE_TYPES[0]);

  const posture = useApi(safeCellPath("get__response_cell_status"));
  const post = posture.data || {};
  const seat = responseCellSeatOf(posture.loading ? null : post);
  /* Soft residual deleted: 503 on cell/cluster is not SoT — posture bus wins. */
  const swarmOff = !seat.engineArmed;
  const armed = seat.engineArmed && (seat.cellReadAllowed || seat.clusterReadAllowed || seat.activateAllowed);

  const incidentsPath = seat.incidentsListAllowed
    ? safeCellPath("get__incidents", { query: { limit: seat.incidentsLimit } })
    : "";
  const incidents = useApi(incidentsPath);
  const rawRows = (incidents.data && (incidents.data.items || incidents.data.incidents)) || [];
  const rows = Array.isArray(rawRows) ? rawRows.slice(0, seat.incidentsLimit) : [];
  const pick = (row) => {
    setIncidentId(String(row.incident_id || row.id || "").trim());
    setDeviceId(String(row.device_id || "").trim());
    setAlarmId(String(row.alarm_id || "").trim());
  };

  const cellPath = (seat.cellReadAllowed && incidentId)
    ? safeCellPath("get__incidents_p_cell", { pathParams: [incidentId] }) : null;
  const clusterPath = (seat.clusterReadAllowed && incidentId)
    ? safeCellPath("get__incidents_p_cluster", { pathParams: [incidentId] }) : null;
  const affinityPath = (seat.clusterReadAllowed && incidentId)
    ? safeCellPath("get__incidents_p_cluster_affinity", { pathParams: [incidentId] }) : null;
  const stigPath = (seat.stigmergyReadAllowed && incidentId)
    ? safeCellPath("get__incidents_p_stigmergy", { pathParams: [incidentId] }) : null;
  const auditPath = (seat.auditReadAllowed && incidentId)
    ? safeCellPath("get__incidents_p_swarm-audit", { pathParams: [incidentId] }) : null;

  const cell = useApi(cellPath);
  const cluster = useApi(clusterPath);
  const affinity = useApi(affinityPath);
  const stig = useApi(stigPath);
  const audit = useApi(auditPath);

  const item = (cell.data && (cell.data.item || cell.data)) || null;
  const cellObj = (item && (item.cell || item)) || null;
  const cellRoles = (item && (item.role_allocations || item.roles)) || [];
  const { members, roles: clusterRoles, priority: livePriority } = clusterMembersOf(cluster.data);
  const stigRows = stigmergyRowsOf(stig.data).slice(0, seat.trailPaintMax);
  const auditRows = auditRowsOf(audit.data).slice(0, seat.auditPaintMax);
  const affinityAssignments = (affinity.data && (affinity.data.assignments || [])) || [];
  const affinityCandidates = (affinity.data && (affinity.data.candidates || [])) || [];
  const memberPaint = (members.length ? members : clusterRoles).slice(0, seat.membersPaintMax);

  const reloadAll = () => {
    cell.reload && cell.reload();
    cluster.reload && cluster.reload();
    affinity.reload && affinity.reload();
    stig.reload && stig.reload();
    audit.reload && audit.reload();
  };

  const activate = async () => {
    if (!seat.activateAllowed) {
      toast("Activate sealed", "warn");
      return;
    }
    if (!incidentId) { toast("Pick an incident first", "warn"); return; }
    // Soft≠Live: server floors risk to SWARM_CELL_MIN_RISK_SCORE on explicit
    // activate; still send risk when the catalog row carries one so Live score sticks.
    const liveRisk = Number(
      (rows.find((r) => String(r.incident_id || r.id || "") === incidentId) || {}).risk_score
    );
    const out = await run(safeCellPath("post__incidents_p_cell_activate", { pathParams: [incidentId] }), {
      body: {
        device_id: deviceId || undefined,
        alarm_id: alarmId || undefined,
        event_type: "alarm.trigger",
        risk_score: Number.isFinite(liveRisk) && liveRisk > 0 ? liveRisk : undefined,
      },
    });
    if (!out) {
      toast(swarmOff || /swarm|503/i.test(error || "")
        ? "SWARM_COORDINATION_ENABLED off — cell routes not armed"
        : "Could not activate cell", "bad");
      return;
    }
    reloadAll();
    toast("Cell activated · " + incidentId, "go");
  };

  const resolve = async () => {
    if (!seat.resolveAllowed) {
      toast("Resolve sealed", "warn");
      return;
    }
    if (!incidentId) { toast("Pick an incident first", "warn"); return; }
    const out = await run(safeCellPath("post__incidents_p_cell_resolve", { pathParams: [incidentId] }), {
      body: { status: "closed" },
    });
    if (!out) { toast("Could not resolve cell", "bad"); return; }
    reloadAll();
    toast("Cell swarm resolved · Incident SoR unchanged", "warn");
  };

  const join = async () => {
    if (!seat.joinAllowed) {
      toast("Join is sealed on this seat", "warn");
      return;
    }
    if (!incidentId || !resourceId) { toast("Incident + resource_id required", "warn"); return; }
    const capList = caps.split(",").map((s) => s.trim()).filter(Boolean);
    const out = await run(safeCellPath("post__incidents_p_cluster_join", { pathParams: [incidentId] }), {
      body: {
        resource_id: resourceId,
        resource_kind: resourceKind || "device",
        capabilities: capList.length ? capList : undefined,
      },
    });
    if (!out) { toast("Cluster join refused — invented “joined” forbidden", "bad"); return; }
    reloadAll();
    toast("Joined cluster · " + resourceId, "go");
  };

  const leave = async () => {
    if (!seat.leaveAllowed) {
      toast("Leave sealed", "warn");
      return;
    }
    if (!incidentId || !resourceId) { toast("Incident + resource_id required", "warn"); return; }
    const out = await run(safeCellPath("post__incidents_p_cluster_leave", { pathParams: [incidentId] }), {
      body: { resource_id: resourceId },
    });
    if (!out) { toast("Could not leave cluster", "bad"); return; }
    reloadAll();
    toast("Left cluster · " + resourceId, "warn");
  };

  const reallocate = async () => {
    if (!seat.reallocateAllowed) {
      toast("Reallocate sealed", "warn");
      return;
    }
    if (!incidentId) { toast("Pick an incident first", "warn"); return; }
    const out = await run(safeCellPath("post__incidents_p_cluster_reallocate", { pathParams: [incidentId] }), {
      body: {},
    });
    if (!out) { toast("Could not reallocate", "bad"); return; }
    reloadAll();
    toast("Roles reallocated", "go");
  };

  const setPrio = async () => {
    if (!seat.priorityAllowed) {
      toast("Priority sealed", "warn");
      return;
    }
    if (!incidentId || !priority) { toast("Incident + priority required", "warn"); return; }
    const out = await run(safeCellPath("patch__incidents_p_cluster_priority", { pathParams: [incidentId] }), {
      method: "PATCH",
      body: { priority },
    });
    if (!out) { toast("Could not set priority", "bad"); return; }
    reloadAll();
    toast("Priority → " + priority, "go");
  };

  const postStig = async () => {
    if (!seat.stigmergyWriteAllowed) {
      toast("Trail sealed", "warn");
      return;
    }
    if (!incidentId || !traceType) { toast("Incident + trace_type required", "warn"); return; }
    const out = await run(safeCellPath("post__incidents_p_stigmergy", { pathParams: [incidentId] }), {
      body: { trace_type: traceType, detail: {} },
    });
    if (!out) { toast("Could not post trail mark", "bad"); return; }
    stig.reload && stig.reload();
    audit.reload && audit.reload();
    toast("Trail mark · " + traceType, "go");
  };

  const panes = [
    ["unit", "Response unit"],
    ["team", "Team"],
    ["trail", "Trail & audit"],
  ];

  const reRead = () => {
    posture.reload && posture.reload();
    incidents.reload && incidents.reload();
    if (incidentId) reloadAll();
    toast && toast("Cell refreshed", "go");
  };

  const p = post;
  const detailSay = !seat.enabled
    ? "This cell seat is closed for your account."
    : swarmOff
      ? "Coordination is off on the server. Cell and cluster stay sealed until armed."
      : seat.detail === "cell_admin_only"
        ? "Writes need a manager seat. You may still browse the incident catalog when allowed."
        : "Pick an incident, then open the unit, cluster, trail, or audit panes.";

  return html`<div className="swarm-desk swarm-desk--liquid"
    data-cmc-page="page107-swarm" data-cmc-residual="page186-cell">
    <div className="swarm-desk__rail">
      <div>
        <p className="eyebrow">Response · Incident cell</p>
        <h2 className="swarm-desk__title">${swarmOff ? "Swarm unarmed" : seat.writeAllowed ? "Live incident cell" : "Cell seat sealed"}</h2>
        <p className="note">${swarmOff
          ? "Joined cells are not invented while coordination is off."
          : "Unit · cluster · trail · audit for the open incident."}</p>
      </div>
      <div className="btn-row swarm-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || incidents.loading || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
    </div>

    <${Alert} tone=${swarmOff || seat.detail === "cell_admin_only" ? "warn" : "info"}
      title=${swarmOff
        ? "Coordination off"
        : "Incident cell"}
      say=${detailSay}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="swarm-desk__kpis">
      <div className="swarm-desk__kpi" data-on=${seat.engineArmed ? "1" : "0"}>
        <span>Flag</span>
        <b>${posture.loading ? "…" : (seat.engineArmed ? "on" : "off")}</b>
        <em>deployment</em>
      </div>
      <div className="swarm-desk__kpi" data-on=${armed || !!(p && p.read_mounted) ? "1" : "0"}>
        <span>Buses</span>
        <b>${p && p.read_mounted ? "mounted" : (armed ? "admin" : "unmounted")}</b>
        <em>Live cell/cluster</em>
      </div>
      <div className="swarm-desk__kpi" data-on=${seat.writeAllowed ? "1" : "0"}>
        <span>Write seat</span>
        <b>${seat.writeAllowed ? "yes" : "no"}</b>
        <em>honesty</em>
      </div>
      <div className="swarm-desk__kpi" data-on="0">
        <span>Your seat</span>
        <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
        <em>site data only</em>
      </div>
    </div>

    ${posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}
    ${seat.detail ? html`<p className="note">detail · ${seat.detail}</p>` : null}

    <div className="chips" style=${{ marginBottom: 12 }}>
      ${panes.map(([id, l]) => html`
        <button key=${id} type="button" className="chip" data-tone="ai" data-sel=${pane===id?"1":"0"}
          onClick=${()=>setPane(id)}>${l}</button>`)}
    </div>

    <${IncidentPicker} rows=${rows} incidentId=${incidentId} onPick=${pick}
      loading=${incidents.loading} error=${incidents.error}
      sealed=${!seat.incidentsListAllowed}/>

    <${Card} title="Incident scope" meta=${incidentId || "none"} cls="mt">
      <${Field} label="Incident id">
        <${Input} icon="book" value=${incidentId} onChange=${e=>setIncidentId(e.target.value.trim())}/>
      <//>
      <${Field} label="Device id" hint="Optional · activate scope">
        <${Input} icon="chip" value=${deviceId} onChange=${e=>setDeviceId(e.target.value.trim())}/>
      <//>
      <${Field} label="Alarm id" hint="Optional">
        <${Input} icon="alert" value=${alarmId} onChange=${e=>setAlarmId(e.target.value.trim())}/>
      <//>
    <//>

    ${pane === "unit" && html`
      <${Card} title="Activate / resolve" meta="Live cell" cls="mt">
        <div className="btn-row">
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !incidentId || !seat.activateAllowed} onClick=${activate}>
            ${busy ? "…" : "Activate cell"}</button>
          <button type="button" className="btn btn--danger btn--sm"
            disabled=${busy || !incidentId || !seat.resolveAllowed} onClick=${resolve}>
            Resolve swarm cell</button>
        </div>
        <p className="note">Cell aggregate only · Incident SoR unchanged</p>
        ${error && html`<${Alert} tone="bad" title="Cell action failed" say=${softResponseErrorOf(error)}/>`}
      <//>
      <${Card} title="Cell state" meta="cell detail" cls="mt">
        ${!incidentId
          ? html`<p className="note">Select an incident.</p>`
          : !seat.cellReadAllowed
            ? html`<p className="note">Cell read sealed.</p>`
          : cell.loading && !cell.data
            ? html`<p className="note">Loading cell…</p>`
            : cell.error
              ? html`<p className="note">${softResponseErrorOf(cell.error)}</p>`
              : html`<${F}>
                  <${KV} k="cell_id" v=${(cellObj && (cellObj.cell_id || cellObj.id)) || "—"}/>
                  <${KV} k="state" v=${(cellObj && cellObj.state) || "—"}/>
                  <${KV} k="risk" v=${cellObj && cellObj.risk_score != null ? String(cellObj.risk_score) : "—"}/>
                  <p className="sub" style=${{ marginTop:12 }}>CLUSTER_ROLES: ${CLUSTER_ROLES.join(" · ")}</p>
                  <div className="rows" style=${{ marginTop:8 }}>
                    ${!cellRoles.length
                      ? html`<p className="note">No role_allocations yet — activate to allocate.</p>`
                      : cellRoles.map((a) => html`
                          <div key=${(a.role || "") + (a.resource_id || "")} className="row row--say">
                            <${Ico} d="users" w=${14}/>
                            <span className="main">
                              <b>${a.role || "—"}</b>
                              <span>${a.resource_id || a.member_id || "unfilled"} · ${a.state || "—"}
                                ${a.fit != null ? " · fit " + a.fit : ""}</span>
                            </span>
                          </div>`)}
                  </div>
                <//>`}
      <//>`}

    ${pane === "team" && html`
      <${Card} title="Cluster team" meta="Live cluster" cls="mt">
        ${!incidentId
          ? html`<p className="note">Select an incident.</p>`
          : !seat.clusterReadAllowed
            ? html`<p className="note">Cluster bus sealed until Live opens the seat.</p>`
          : cluster.error
            ? html`<${Alert} tone="warn" title="Cluster unavailable" say=${softResponseErrorOf(cluster.error)}/>`
            : html`<${F}>
                <${KV} k="Live priority" v=${String(livePriority || "—")}/>
                <${KV} k="Members" v=${String(memberPaint.length)}/>
                <div className="rows" style=${{ marginTop:8 }}>
                  ${!memberPaint.length
                    ? html`<p className="note">Empty cluster — join a resource or activate the cell.</p>`
                    : memberPaint.map((m) => html`
                        <div key=${(m.resource_id || m.role || "") + (m.state || "")} className="row row--say">
                          <${Ico} d="users" w=${14}/>
                          <span className="main">
                            <b>${m.resource_id || m.member_id || m.role || "—"}</b>
                            <span>${m.role || "member"} · ${cmcPlainOf(m.state || m.status || "—")}</span>
                          </span>
                        </div>`)}
                </div>
              <//>`}
        <${Field} label="Resource id" hint="Device / unit to muster or dismiss">
          <${Input} icon="chip" value=${resourceId} onChange=${e=>setResourceId(e.target.value.trim())}/>
        <//>
        <${Field} label="Kind" hint="device · human · actuator · robot">
          <${Input} icon="book" value=${resourceKind} onChange=${e=>setResourceKind(e.target.value.trim())}/>
        <//>
        <${Field} label="Capabilities" hint="comma-separated · join only">
          <${Input} icon="bolt" value=${caps} onChange=${e=>setCaps(e.target.value)}/>
        <//>
        <div className="btn-row" style=${{ marginTop:10 }}>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !incidentId || !seat.joinAllowed} onClick=${join}>Join</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !incidentId || !seat.leaveAllowed} onClick=${leave}>Leave</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !incidentId || !seat.reallocateAllowed} onClick=${reallocate}>Reallocate</button>
        </div>
        <${Field} label="Priority">
          <select className="inp" value=${priority} onChange=${e=>setPriority(e.target.value)}>
            ${PRIORITIES.map((pr) => html`<option key=${pr} value=${pr}>${pr}</option>`)}
          </select>
        <//>
        <button type="button" className="btn btn--sm"
          disabled=${busy || !incidentId || !seat.priorityAllowed} onClick=${setPrio}
          style=${{ marginTop:8 }}>Set priority</button>
      <//>
      <${Card} title="Affinity" meta="Live" cls="mt">
        ${!incidentId
          ? html`<p className="note">Select an incident.</p>`
          : !seat.clusterReadAllowed
            ? html`<p className="note">Affinity bus sealed.</p>`
          : affinity.error
            ? html`<p className="note">${softResponseErrorOf(affinity.error)}</p>`
            : html`<${F}>
                <${KV} k="Assignments" v=${String(affinityAssignments.length)}/>
                <${KV} k="Candidates" v=${String(affinityCandidates.length)}/>
                <p className="note">Live affinity only.</p>
              <//>`}
      <//>`}

    ${pane === "trail" && html`
      <${Card} title="Stigmergy trail" meta="Live" cls="mt">
        <${Field} label="Trace type">
          <select className="inp" value=${traceType} onChange=${e=>setTraceType(e.target.value)}>
            ${STIGMERGY_TRACE_TYPES.map((t) => html`<option key=${t} value=${t}>${t}</option>`)}
          </select>
        <//>
        <button type="button" className="btn btn--go btn--sm"
          disabled=${busy || !incidentId || !seat.stigmergyWriteAllowed} onClick=${postStig}
          style=${{ marginTop:8 }}>Post trail mark</button>
        <div className="rows" style=${{ marginTop:12 }}>
          ${!incidentId
            ? html`<p className="note">Select an incident.</p>`
            : !seat.stigmergyReadAllowed
              ? html`<p className="note">Trail bus sealed.</p>`
            : stig.error
              ? html`<p className="note">${softResponseErrorOf(stig.error)}</p>`
              : !stigRows.length
                ? html`<p className="note">No trail marks yet.</p>`
                : stigRows.map((t) => html`
                    <div key=${t.trace_id || t.created_at_ms || t.trace_type} className="row row--say">
                      <${Ico} d="bolt" w=${14}/>
                      <span className="main">
                        <b>${t.trace_type || "—"}</b>
                        <span>${t.actor || "—"} · ${t.created_at_ms || t.ts || "—"}</span>
                      </span>
                    </div>`)}
        </div>
      <//>
      <${Card} title="Swarm audit" meta="Live audit" cls="mt">
        ${!incidentId
          ? html`<p className="note">Select an incident.</p>`
          : !seat.auditReadAllowed
            ? html`<p className="note">Audit bus sealed.</p>`
          : audit.error
            ? html`<p className="note">${softResponseErrorOf(audit.error)}</p>`
            : html`<div className="rows">
                ${!auditRows.length
                  ? html`<p className="note">No audit rows yet.</p>`
                  : auditRows.map((r, i) => html`
                      <div key=${r.id || r.action + i} className="row row--say">
                        <${Ico} d="book" w=${14}/>
                        <span className="main">
                          <b>${r.action || r.event || "—"}</b>
                          <span>${r.actor || "—"} · ${r.created_at_ms || r.ts || "—"}</span>
                        </span>
                      </div>`)}
              </div>`}
        <p className="note">Append-only ledger when the audit seat is open.</p>
      <//>`}
  </div>`;
}

/* ── Actuators registry + practice select · Soft≠Live Page191 ─────────── */
/** Soft≠Live Page191: missing contract ops must not crash equipment desk. */
function safeEquipPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_equipment_status") return "/response/equipment/status";
    if (op === "get__actuators_resources") return "/actuators/resources";
    if (op === "post__actuators_resources_select") return "/actuators/resources/select";
    return "";
  }
}

/** Pure: seat chips from Live Response Equipment posture (Page191). */
export function responseEquipmentSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const m = Number(lim.items_paint_max);
  return {
    catalogueListAllowed: p.catalogue_list_allowed === true,
    practiceSelectAllowed: p.practice_select_allowed === true,
    catalogPatchAllowed: p.catalog_patch_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    playbookNavAllowed: p.playbook_nav_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    itemsPaintMax: Number.isFinite(m) && m > 0 ? Math.floor(m) : 40,
  };
}

export function ActuatorsDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const { run, busy, error, envelope } = useAction();

  const posture = useApi(safeEquipPath("get__response_equipment_status"));
  const post = posture.data || {};
  const seat = responseEquipmentSeatOf(posture.loading ? null : post);

  const resources = useApi(seat.catalogueListAllowed ? safeEquipPath("get__actuators_resources") : "");
  const [alarmId, setAlarmId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [risk, setRisk] = useState(50);
  const [reason, setReason] = useState("operator practice");
  const [selected, setSelected] = useState(null);

  const items = useMemo(() => {
    const d = resources.data || {};
    const raw = d.items || d.resources || (Array.isArray(d) ? d : []);
    const list = Array.isArray(raw) ? raw : [];
    return list.slice(0, seat.itemsPaintMax);
  }, [resources.data, seat.itemsPaintMax]);

  const practice = async () => {
    if (!seat.practiceSelectAllowed) {
      toast("Practice select sealed", "warn");
      return;
    }
    const out = await run(safeEquipPath("post__actuators_resources_select"), {
      body: {
        alarm_id: alarmId || undefined,
        device_id: deviceId || undefined,
        risk_score: Number(risk) || 0,
        reason: reason || undefined,
        dry_run: true,
      },
    });
    if (!out) {
      toast(error || "Actuator select refused · Live", "bad");
      return;
    }
    setSelected(out);
    toast("Practice select complete", "go");
  };

  const reRead = () => {
    posture.reload && posture.reload();
    if (seat.catalogueListAllowed) resources.reload && resources.reload();
    toast && toast("Equipment refreshed", "go");
  };

  const off = !seat.catalogueListAllowed
    || !!(resources.error && /503|disabled|not.?enabled|actuator|forbidden|403/i.test(String(resources.error || "")));
  const alertTone = seat.detail === "equipment_admin_only" || seat.detail === "actuator_interface_disabled" || off
    ? "warn"
    : "info";
  const alertTitle = seat.detail === "equipment_admin_only"
    ? "Equipment is admin-only on this seat"
    : seat.detail === "actuator_interface_disabled"
      ? "Actuator interface is not armed"
      : off
        ? "Equipment is not armed"
        : "Live equipment registry";
  const alertSay = seat.detail === "equipment_admin_only"
    ? "Catalogue and practice select are sealed for this account."
    : seat.detail === "actuator_interface_disabled"
      ? "Equipment interface is off on the server. Nothing is invented here."
      : off
        ? "Equipment routes are unavailable right now."
        : "Browse the catalogue and practice-select resources when armed.";

  return html`<div className="prep-desk prep-desk--liquid equip-desk equip-desk--liquid"
    data-cmc-page="page191-equipment" data-cmc-residual="page191-equipment">
    <div className="prep-desk__rail">
      <div>
        <p className="eyebrow">Response · Equipment</p>
        <h2 className="prep-desk__title">${seat.catalogueListAllowed ? "Equipment registry" : "Equipment seat sealed"}</h2>
        <p className="note">Catalogue and practice select. Empty means none visible.</p>
      </div>
      <div className="btn-row prep-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || resources.loading || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
    </div>

    <${Alert} tone=${alertTone} title=${alertTitle} say=${alertSay}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="prep-desk__kpis">
      <div className="prep-desk__kpi" data-on=${seat.catalogueListAllowed ? "1" : "0"}>
        <span>Catalogue</span>
        <b>${seat.catalogueListAllowed ? "open" : "sealed"}</b>
        <em>/actuators/resources</em>
      </div>
      <div className="prep-desk__kpi" data-on=${seat.practiceSelectAllowed ? "1" : "0"}>
        <span>Practice</span>
        <b>${seat.practiceSelectAllowed ? "open" : "sealed"}</b>
        <em>practice select</em>
      </div>
      <div className="prep-desk__kpi" data-on=${seat.catalogPatchAllowed ? "1" : "0"}>
        <span>Catalog write</span>
        <b>${seat.catalogPatchAllowed ? "open" : "sealed"}</b>
        <em>PATCH bus</em>
      </div>
      <div className="prep-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
        <em>site data only</em>
      </div>
    </div>

    ${posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}
    ${seat.detail ? html`<p className="note">detail · ${seat.detail}</p>` : null}

    <${Card} title="Registry" meta="equipment list">
      ${!seat.catalogueListAllowed
        ? html`<p className="note">Catalogue sealed until Live opens the seat.</p>`
        : resources.loading && !resources.data
          ? html`<p className="note">Loading…</p>`
          : resources.error
            ? html`<${Alert} tone="warn" title="Resources unavailable" say=${softResponseErrorOf(resources.error)}/>`
            : html`<div className="rows">
                ${!items.length
                  ? html`<p className="note">No equipment registered yet.</p>`
                  : items.map((r, i) => html`
                      <div key=${r.resource_id || r.id || i} className="row row--say">
                        <${Ico} d="bolt" w=${14}/>
                        <span className="main">
                          <b>${r.kind || r.resource_kind || "actuator"}</b>
                          <span>${r.resource_id || r.id || r.kind || "—"} · ${actuatorStatusPlainOf(r)}
                            ${Array.isArray(r.capabilities) ? " · " + r.capabilities.slice(0, 4).join(",") : ""}</span>
                        </span>
                      </div>`)}
              </div>`}
    <//>

    <${Card} title="Practice select" meta="practice only" cls="mt">
      ${!seat.practiceSelectAllowed
        ? html`<p className="note">Practice select sealed.</p>`
        : html`<${F}>
            <${Field} label="Alarm id"><${Input} icon="alert" value=${alarmId} onChange=${e=>setAlarmId(e.target.value.trim())}/><//>
            <${Field} label="Device id"><${Input} icon="chip" value=${deviceId} onChange=${e=>setDeviceId(e.target.value.trim())}/><//>
            <${Field} label="Risk score">
              <${Input} icon="bolt" type="number" value=${String(risk)} onChange=${e=>setRisk(+e.target.value)}/>
            <//>
            <${Field} label="Reason"><${Input} icon="book" value=${reason} onChange=${e=>setReason(e.target.value)}/><//>
            <button type="button" className="btn btn--go btn--sm" disabled=${busy} onClick=${practice} style=${{ marginTop:10 }}>
              ${busy ? "Selecting…" : "Practice select"}</button>
            ${error && html`<${Alert} tone="bad" title="Select failed" say=${softResponseErrorOf(error)}/>`}
            ${selected && html`<${Sec} title="Selection result" meta=${selected.dry_run || selected.mode === "dry_run" ? "practice" : "live"}>
              <${KV} k="kind" v=${(selected.selected && (selected.selected.kind || selected.selected.resource_kind)) || selected.kind || selected.resource_kind || selected.selected_kind || "—"}/>
              <${KV} k="resource" v=${(selected.selected && (selected.selected.resource_id || selected.selected.id)) || selected.resource_id || selected.selected_resource_id || "—"}/>
              <${KV} k="status" v=${actuatorStatusPlainOf(selected.selected || selected)
                || cmcPlainOf(selected.result)
                || (selected.selected == null ? "none" : "—")}/>
            <//>`}
          <//>`}
      <button type="button" className="btn btn--sm btn--go" style=${{ marginTop:10 }}
        disabled=${!seat.playbookNavAllowed}
        onClick=${()=>go("/response/sop")}>
        Open playbook</button>
      <p className="note">Opens the playbook tab.</p>
    <//>
  </div>`;
}

/* ── External robots — Wave 27 desk lives in external-fleet-desk.js ────── */
export { RobotsDesk, ExternalFleetDesk } from "./external-fleet-desk.js";

